/**
 * Mobile Navigation Component - Optimized for touch interactions and mobile UX
 * Features: Bottom navigation, swipe gestures, haptic feedback, accessibility
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Users,
  FolderOpen,
  Calendar,
  Menu,
  X,
  Settings,
  User,
  Bell,
  Search,
  Plus
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: number;
  isNew?: boolean;
}

interface MobileNavigationProps {
  className?: string;
  onItemSelect?: (itemId: string) => void;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Home,
    path: '/'
  },
  {
    id: 'customers',
    label: 'Clients',
    icon: Users,
    path: '/customers'
  },
  {
    id: 'projects',
    label: 'Commissions',
    icon: FolderOpen,
    path: '/projects'
  },
  {
    id: 'appointments',
    label: 'Calendar',
    icon: Calendar,
    path: '/appointments'
  }
];

const quickActions = [
  {
    id: 'new-customer',
    label: 'New Client',
    icon: Users,
    path: '/customers/new'
  },
  {
    id: 'new-project',
    label: 'New Commission',
    icon: FolderOpen,
    path: '/projects/new'
  },
  {
    id: 'new-appointment',
    label: 'New Consultation',
    icon: Calendar,
    path: '/appointments/new'
  }
];

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  className,
  onItemSelect
}) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const [lastTap, setLastTap] = useState(0);

  // Handle double-tap for quick actions
  const handleDoubleTap = (callback: () => void) => {
    const now = Date.now();
    const timeBetweenTaps = now - lastTap;
    
    if (timeBetweenTaps < 300 && timeBetweenTaps > 0) {
      callback();
      // Haptic feedback for supported devices
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    }
    
    setLastTap(now);
  };

  // Close menus when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsQuickActionsOpen(false);
  }, [location.pathname]);

  // Handle gesture events
  useEffect(() => {
    let startY = 0;
    let startX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const endY = e.changedTouches[0].clientY;
      const endX = e.changedTouches[0].clientX;
      const deltaY = startY - endY;
      const deltaX = Math.abs(startX - endX);

      // Swipe up to open quick actions (only if at bottom of screen)
      if (deltaY > 50 && deltaX < 100 && startY > window.innerHeight - 100) {
        e.preventDefault();
        setIsQuickActionsOpen(true);
      }

      // Swipe down to close quick actions
      if (deltaY < -50 && deltaX < 100 && isQuickActionsOpen) {
        e.preventDefault();
        setIsQuickActionsOpen(false);
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isQuickActionsOpen]);

  const handleItemSelect = (itemId: string) => {
    onItemSelect?.(itemId);
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(25);
    }
  };

  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav
        role="tablist"
        aria-label="Mobile primary navigation"
        className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-white border-t border-gray-200",
        "px-2 py-1 pb-safe",
        "shadow-lg backdrop-blur-md bg-opacity-95",
        "md:hidden", // Hide on desktop
        className
      )}
      >
        <div className="flex items-center justify-around max-w-md mx-auto">
          {navigationItems.map((item) => {
            const isActive = isActiveRoute(item.path);
            const Icon = item.icon;

            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  "relative flex flex-col items-center justify-center",
                  "min-w-[60px] py-2 px-1",
                  "rounded-lg transition-all duration-200",
                  "active:scale-95 active:bg-gray-100",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                  isActive 
                    ? "text-blue-600" 
                    : "text-gray-500 hover:text-gray-700"
                )}
                onClick={() => handleItemSelect(item.id)}
                aria-label={item.label}
                role="tab"
                aria-selected={isActive}
              >
                {/* Active indicator */}
                {isActive && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute -top-1 w-8 h-1 bg-blue-600 rounded-full"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}

                {/* Icon with animation */}
                <motion.div
                  animate={{
                    scale: isActive ? 1.1 : 1,
                    y: isActive ? -2 : 0
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  className="relative"
                >
                  <Icon 
                    className={cn(
                      "w-6 h-6 mb-1",
                      isActive ? "text-blue-600" : "text-gray-500"
                    )} 
                  />
                  
                  {/* Badge */}
                  {item.badge && item.badge > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium"
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </motion.span>
                  )}

                  {/* New indicator */}
                  {item.isNew && (
                    <motion.span
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"
                    />
                  )}
                </motion.div>

                {/* Label */}
                <span className={cn(
                  "text-xs font-medium leading-none",
                  isActive ? "text-blue-600" : "text-gray-500"
                )}>
                  {item.label}
                </span>
              </Link>
            );
          })}

          {/* Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            onDoubleClick={() => handleDoubleTap(() => setIsQuickActionsOpen(true))}
            className={cn(
              "relative flex flex-col items-center justify-center",
              "min-w-[60px] py-2 px-1",
              "rounded-lg transition-all duration-200",
              "active:scale-95 active:bg-gray-100",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
              "text-gray-500 hover:text-gray-700"
            )}
            aria-label="Open menu"
            aria-expanded={isMenuOpen}
            data-testid="mobile-nav-toggle"
          >
            <motion.div
              animate={{ rotate: isMenuOpen ? 90 : 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 mb-1" />
              ) : (
                <Menu className="w-6 h-6 mb-1" />
              )}
            </motion.div>
            <span className="text-xs font-medium leading-none">
              Menu
            </span>
          </button>
        </div>
      </nav>

      {/* Overlay Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm md:hidden"
            onClick={() => setIsMenuOpen(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-6 pb-safe"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" />
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Link
                  to="/profile"
                  className="flex flex-col items-center p-4 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User className="w-8 h-8 text-gray-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700">Profile</span>
                </Link>

                <Link
                  to="/settings"
                  className="flex flex-col items-center p-4 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Settings className="w-8 h-8 text-gray-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700">Settings</span>
                </Link>

                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    // Add search functionality
                  }}
                  className="flex flex-col items-center p-4 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <Search className="w-8 h-8 text-gray-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700">Search</span>
                </button>
              </div>

              <div className="text-center text-sm text-gray-500">
                <p>Swipe up on navigation for quick actions</p>
                <p className="mt-1">Double-tap menu for shortcuts</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Actions Overlay */}
      <AnimatePresence>
        {isQuickActionsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm md:hidden"
            onClick={() => setIsQuickActionsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
              className="absolute bottom-20 left-4 right-4 bg-white rounded-2xl p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                <button
                  onClick={() => setIsQuickActionsOpen(false)}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  
                  return (
                    <motion.div
                      key={action.id}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={action.path}
                        className="flex items-center p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 active:scale-95 transition-all duration-200"
                        onClick={() => {
                          setIsQuickActionsOpen(false);
                          handleItemSelect(action.id);
                        }}
                      >
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm mr-4">
                          <Icon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{action.label}</h4>
                          <p className="text-sm text-gray-500">Create new {action.label.toLowerCase()}</p>
                        </div>
                        <Plus className="w-5 h-5 text-gray-400 ml-auto" />
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              <div className="mt-4 text-center text-xs text-gray-500">
                <p>Swipe down to close</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Safe area padding for devices with home indicator */}
      <style>{`
        .pb-safe {
          padding-bottom: max(8px, env(safe-area-inset-bottom));
        }
      `}</style>
    </>
  );
};

export default MobileNavigation;
